import { useState } from 'react';
import { User, Mail, Phone, Building2, Briefcase, Shield, Calendar, Edit2, Save, X } from 'lucide-react';
import useAuthStore from '../../stores/authStore';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Avatar from '../../components/common/Avatar';
import './UserProfile.css';

export default function UserProfile() {
    const { user } = useAuthStore();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        fullName: user?.fullName || '',
        email: user?.email || '',
        phone: user?.phone || '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        // TODO: Call API to update profile
        console.log('Saving profile:', formData);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setFormData({
            fullName: user?.fullName || '',
            email: user?.email || '',
            phone: user?.phone || '',
        });
        setIsEditing(false);
    };

    return (
        <div className="user-profile-page">
            <div className="page-header">
                <h1>My Profile</h1>
                <p className="page-subtitle">View and manage your account information</p>
            </div>

            <div className="profile-content">
                {/* Profile Card */}
                <Card className="profile-card">
                    <div className="profile-header">
                        <div className="profile-avatar-section">
                            <Avatar name={user?.fullName} size="xl" />
                            <div className="profile-name-section">
                                <h2>{user?.fullName}</h2>
                                <span className="profile-role">
                                    <Shield size={14} />
                                    {user?.role}
                                </span>
                            </div>
                        </div>
                        {!isEditing ? (
                            <Button
                                variant="outline"
                                onClick={() => setIsEditing(true)}
                            >
                                <Edit2 size={16} />
                                Edit Profile
                            </Button>
                        ) : (
                            <div className="profile-actions">
                                <Button variant="outline" onClick={handleCancel}>
                                    <X size={16} />
                                    Cancel
                                </Button>
                                <Button onClick={handleSave}>
                                    <Save size={16} />
                                    Save Changes
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="profile-divider" />

                    <div className="profile-info-grid">
                        <div className="profile-info-item">
                            <div className="info-icon">
                                <User size={20} />
                            </div>
                            <div className="info-content">
                                <label>Full Name</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        className="profile-input"
                                    />
                                ) : (
                                    <span>{user?.fullName || '-'}</span>
                                )}
                            </div>
                        </div>

                        <div className="profile-info-item">
                            <div className="info-icon">
                                <Mail size={20} />
                            </div>
                            <div className="info-content">
                                <label>Email Address</label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="profile-input"
                                    />
                                ) : (
                                    <span>{user?.email || '-'}</span>
                                )}
                            </div>
                        </div>

                        <div className="profile-info-item">
                            <div className="info-icon">
                                <Phone size={20} />
                            </div>
                            <div className="info-content">
                                <label>Phone Number</label>
                                {isEditing ? (
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="profile-input"
                                    />
                                ) : (
                                    <span>{user?.phone || '-'}</span>
                                )}
                            </div>
                        </div>

                        <div className="profile-info-item">
                            <div className="info-icon">
                                <Building2 size={20} />
                            </div>
                            <div className="info-content">
                                <label>Department</label>
                                <span>{user?.department || '-'}</span>
                            </div>
                        </div>

                        <div className="profile-info-item">
                            <div className="info-icon">
                                <Briefcase size={20} />
                            </div>
                            <div className="info-content">
                                <label>Position</label>
                                <span>{user?.position || '-'}</span>
                            </div>
                        </div>

                        <div className="profile-info-item">
                            <div className="info-icon">
                                <Calendar size={20} />
                            </div>
                            <div className="info-content">
                                <label>Employee ID</label>
                                <span>{user?.employeeId || '-'}</span>
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Account Status Card */}
                <Card className="status-card">
                    <h3>Account Status</h3>
                    <div className="status-grid">
                        <div className="status-item">
                            <span className="status-label">Account Status</span>
                            <span className={`status-badge ${user?.isActive ? 'active' : 'inactive'}`}>
                                {user?.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                        <div className="status-item">
                            <span className="status-label">Role</span>
                            <span className="status-value">{user?.role}</span>
                        </div>
                        <div className="status-item">
                            <span className="status-label">Last Login</span>
                            <span className="status-value">
                                {user?.lastLoginAt 
                                    ? new Date(user.lastLoginAt).toLocaleString('id-ID')
                                    : 'N/A'
                                }
                            </span>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
